/**
 * /ninety/calendar — read-only Outlook-backed team calendar (F58 Phase 4).
 *
 * Agenda list filterable by:
 *   • My Calendar — the signed-in user's own mailbox (every event; colored by
 *     person/organizer).
 *   • each team the user belongs to — only real team WORK / meeting events
 *     (calls always show; social/all-company events are filtered out server-side
 *     in _calendar-filter.ts). Colored by team.
 *
 * Non-admins only see chips for teams they're a member of; admins see all teams.
 *
 * Degrades gracefully when Microsoft Graph isn't configured/consented yet — the
 * API reports `configured: false` and we show a friendly banner instead of an
 * error.
 */

import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Card, CardBody } from '~/components/ui';
import { useAuth } from '~/lib/auth';
import { listTeams, type Team, type TeamMember } from '~/lib/api/teams';
import { getTeamCalendar, type CalendarEvent, MY_CALENDAR } from '~/lib/api/calendar';

export const Route = createFileRoute('/_app/ninety/calendar')({
  component: CalendarTab,
});

// Stable palette for color-coding (team in team views, person in My Calendar).
const PALETTE = [
  '#2563eb', // blue
  '#16a34a', // green
  '#db2777', // pink
  '#d97706', // amber
  '#7c3aed', // violet
  '#0891b2', // cyan
  '#dc2626', // red
  '#4f46e5', // indigo
  '#65a30d', // lime
  '#9333ea', // purple
];

/** Stable hash → palette slot, so a given key is ALWAYS the same color. */
function hashColor(key: string): string {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return PALETTE[h % PALETTE.length];
}

/**
 * Team color: index-based among the visible teams (each team a distinct slot).
 * Falls back to a stable hash when the team isn't in the visible list.
 */
function teamColor(teamId: string, index: number): string {
  if (index >= 0) return PALETTE[index % PALETTE.length];
  return hashColor(teamId);
}

/**
 * Per-person color (CAL7): always derived from a stable hash of the person key
 * (display name / email), so the same person is the same color on every load
 * and regardless of event ordering — NOT a positional index.
 */
function personColor(key: string): string {
  return hashColor(key);
}

function CalendarTab() {
  const { profile, hasRole } = useAuth();
  const isAdmin = hasRole('admin');
  const myUserId = (profile as { user_id?: string } | null)?.user_id ?? '';

  const [team, setTeam] = useState<string>(MY_CALENDAR);

  const { data: teamData } = useQuery({
    queryKey: ['teams_with_members'],
    queryFn: () => listTeams(),
  });

  // Restrict the chips: admins see all teams; everyone else sees only teams they
  // are a member of (no cross-team browsing).
  const visibleTeams: Team[] = useMemo(() => {
    const teams: Team[] = teamData?.teams ?? [];
    if (isAdmin) return teams;
    const members: TeamMember[] = teamData?.members ?? [];
    const myTeamIds = new Set(
      members.filter((m) => m.user_id === myUserId).map((m) => m.team_id),
    );
    return teams.filter((t) => myTeamIds.has(t.id));
  }, [teamData, isAdmin, myUserId]);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['team_calendar', team],
    queryFn: () => getTeamCalendar(team),
    staleTime: 5 * 60 * 1000,
  });

  const events = data?.events ?? [];
  const isMine = (data?.view ?? (team === MY_CALENDAR ? 'mine' : 'team')) === 'mine';

  // Color legend: in My Calendar color by organizer (person), in team views all
  // events for the selected team share that team's color.
  const legend = useMemo(() => buildLegend(events, isMine, visibleTeams, team), [events, isMine, visibleTeams, team]);
  const colorOf = useMemo(() => {
    const map = new Map(legend.map((l) => [l.key, l.color]));
    return (e: CalendarEvent) => map.get(legendKeyFor(e, isMine, team)) ?? '#94a3b8';
  }, [legend, isMine, team]);

  const grouped = useMemo(() => groupByDay(events), [events]);

  return (
    <div className="px-4 sm:px-6 py-6 space-y-4">
      <p className="text-sm text-[var(--color-ink-muted)]">
        {isMine
          ? 'Your Outlook calendar. Switch to a team to see that team’s work events & calls.'
          : 'Read-only view of this team’s work events & calls from Outlook. Social/all-company events are hidden.'}
      </p>

      <div className="flex flex-wrap gap-2">
        <FilterChip active={team === MY_CALENDAR} onClick={() => setTeam(MY_CALENDAR)}>
          My Calendar
        </FilterChip>
        {visibleTeams.map((t) => (
          <FilterChip key={t.id} active={team === t.id} onClick={() => setTeam(t.id)}>
            {t.name}
          </FilterChip>
        ))}
      </div>

      {legend.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[var(--color-ink-soft)]">
          <span className="text-[var(--color-ink-muted)]">
            {isMine ? 'By organizer:' : 'Team:'}
          </span>
          {legend.map((l) => (
            <span key={l.key} className="inline-flex items-center gap-1.5">
              <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: l.color }} />
              {l.label}
            </span>
          ))}
        </div>
      )}

      {isLoading && <p className="text-sm text-[var(--color-ink-muted)]">Loading calendar…</p>}

      {isError && (
        <Card>
          <CardBody className="text-sm text-[var(--color-danger,#b91c1c)]">
            Couldn’t load the calendar: {(error as Error)?.message ?? 'unknown error'}
          </CardBody>
        </Card>
      )}

      {!isLoading && !isError && data && !data.configured && <NotConnectedBanner detail={data.notConnectedDetail} />}

      {!isLoading && !isError && data?.configured && grouped.length === 0 && (
        <p className="text-sm text-[var(--color-ink-muted)]">
          {isMine ? 'No events in the next 14 days.' : 'No team work events or calls in the next 14 days.'}
        </p>
      )}

      {!isLoading && !isError && data?.configured && grouped.length > 0 && (
        <div className="space-y-5">
          {grouped.map(([day, dayEvents]) => (
            <div key={day} className="space-y-2">
              <h3 className="text-sm font-semibold text-[var(--color-navy)] sticky top-0 bg-[var(--color-surface,#fff)] py-1">
                {day}
              </h3>
              <div className="space-y-2">
                {dayEvents.map((e) => <EventCard key={e.id} e={e} color={colorOf(e)} />)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function NotConnectedBanner({ detail }: { detail?: string }) {
  return (
    <Card>
      <CardBody className="space-y-1">
        <div className="font-semibold text-[var(--color-navy)]">Outlook calendar not connected yet</div>
        <p className="text-sm text-[var(--color-ink-soft)]">
          An admin must grant the Microsoft Graph <code>Calendars.Read</code> application permission
          (with admin consent) for the PDQ Azure app, then redeploy. Once connected, this tab shows
          your calendar and your teams’ work events from Outlook.
        </p>
        {detail && <p className="text-xs text-[var(--color-ink-muted)]">{detail}</p>}
      </CardBody>
    </Card>
  );
}

function EventCard({ e, color }: { e: CalendarEvent; color: string }) {
  return (
    <Card>
      <CardBody>
        <div className="space-y-1.5 border-l-4 pl-3" style={{ borderColor: color }}>
        <div className="flex items-start justify-between gap-3">
          <span className="font-semibold flex items-center gap-2">
            {e.isOnCall && (
              <span className="rounded bg-[var(--color-danger,#b91c1c)] px-1.5 py-0.5 text-[10px] font-medium text-white">
                On-Call
              </span>
            )}
            {e.isCall && !e.isOnCall && (
              <span className="rounded bg-[var(--color-navy)] px-1.5 py-0.5 text-[10px] font-medium text-white">
                Call
              </span>
            )}
            {e.subject}
          </span>
          <span className="text-xs text-[var(--color-ink-muted)] shrink-0">{timeRange(e)}</span>
        </div>
        <div className="text-sm text-[var(--color-ink-soft)] flex flex-wrap gap-x-4 gap-y-1">
          {e.organizerName && <span>Organizer: {e.organizerName}</span>}
          <span>{e.attendeeCount} attendee{e.attendeeCount === 1 ? '' : 's'}</span>
        </div>
        {e.categories.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {e.categories.map((c) => (
              <span key={c} className="rounded bg-[var(--color-surface-2)] px-1.5 py-0.5 text-[10px] text-[var(--color-ink-soft)]">
                {c}
              </span>
            ))}
          </div>
        )}
        {e.webLink && (
          <div>
            <a
              href={e.webLink}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-[var(--color-navy)] hover:underline"
            >
              Open in Outlook ↗
            </a>
          </div>
        )}
        </div>
      </CardBody>
    </Card>
  );
}

// ── color legend helpers ───────────────────────────────────────────────────────

interface LegendItem { key: string; label: string; color: string; }

/** In My Calendar, the legend/color key is the organizer; in team views it's the team. */
function legendKeyFor(e: CalendarEvent, isMine: boolean, team: string): string {
  if (isMine) return (e.organizerName || e.organizerEmail || 'You').trim();
  return team;
}

function buildLegend(
  events: CalendarEvent[],
  isMine: boolean,
  teams: Team[],
  team: string,
): LegendItem[] {
  if (isMine) {
    const keys: string[] = [];
    for (const e of events) {
      const k = legendKeyFor(e, true, team);
      if (!keys.includes(k)) keys.push(k);
    }
    // CAL7: each person gets a STABLE color (hash of their key), so the same
    // person is always the same color regardless of order or which events load.
    return keys.map((k) => ({ key: k, label: k, color: personColor(k) }));
  }
  // Team view — single team, single color (index by position among visible teams).
  const idx = teams.findIndex((t) => t.id === team);
  const label = teams.find((t) => t.id === team)?.name ?? 'Team';
  if (events.length === 0) return [];
  return [{ key: team, label, color: teamColor(team, idx) }];
}

// ── date helpers ───────────────────────────────────────────────────────────────

function parseGraphDate(s: string | null): Date | null {
  if (!s) return null;
  // Graph dateTime has no zone suffix; it pairs with a separate timeZone field.
  // For display ordering/grouping we treat it as a wall-clock local time.
  const d = new Date(s.endsWith('Z') ? s : s + 'Z');
  return Number.isNaN(d.getTime()) ? null : d;
}

function groupByDay(events: CalendarEvent[]): [string, CalendarEvent[]][] {
  const map = new Map<string, CalendarEvent[]>();
  for (const e of events) {
    const d = parseGraphDate(e.start);
    const key = d
      ? d.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' })
      : 'Undated';
    const arr = map.get(key) ?? [];
    arr.push(e);
    map.set(key, arr);
  }
  return Array.from(map.entries());
}

function timeRange(e: CalendarEvent): string {
  const s = parseGraphDate(e.start);
  const en = parseGraphDate(e.end);
  if (!s) return '';
  const fmt = (d: Date) => d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return en ? `${fmt(s)} – ${fmt(en)}` : fmt(s);
}

function FilterChip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={
        'rounded-full px-3 py-1 text-xs border transition-colors ' +
        (active
          ? 'bg-[var(--color-navy)] text-white border-[var(--color-navy)]'
          : 'border-[var(--color-line)] text-[var(--color-ink-soft)] hover:bg-[var(--color-surface-2)]')
      }
    >
      {children}
    </button>
  );
}
